import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { 
  trackInviteCodeGenerated, 
  trackInviteCodeRedeemed, 
  trackInviteCodeFailed, 
  trackInviteCodeDeactivated 
} from '@/lib/analytics';
export interface InviteCode {
  id: string;
  code: string;
  tier: string;
  duration_days: number;
  max_uses: number | null;
  current_uses: number;
  campaign: string | null;
  source: string | null;
  notes: string | null;
  is_active: boolean;
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface InviteCodeRedemption {
  id: string;
  code_id: string;
  user_id: string;
  tier_granted: string;
  duration_days_granted: number;
  beta_expires_at: string;
  redeemed_at: string;
  ip_address: string | null;
  user_agent: string | null;
  // Joined data
  invite_code?: { code: string; campaign: string | null };
  user_email?: string;
  user_name?: string;
}

export interface CreateInviteCodeInput {
  code?: string;
  tier?: 'pro' | 'beta_pro';
  duration_days?: number;
  max_uses?: number | null;
  campaign?: string;
  source?: string;
  notes?: string;
  expires_at?: string | null;
}

export function useInviteCodes() {
  const { user } = useAuth();
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [redemptions, setRedemptions] = useState<InviteCodeRedemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await supabase
        .from('invite_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setCodes(data || []);
    } catch (err: any) {
      console.error('Error fetching invite codes:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRedemptions = useCallback(async (codeId?: string) => {
    try {
      let query = supabase
        .from('invite_code_redemptions')
        .select(`
          *,
          invite_codes (code, campaign)
        `)
        .order('redeemed_at', { ascending: false });

      if (codeId) {
        query = query.eq('code_id', codeId);
      }

      const { data, error: fetchError } = await query.limit(100);

      if (fetchError) throw fetchError;

      // Fetch user info for redemptions
      const userIds = [...new Set(data?.map(r => r.user_id) || [])];
      const userMap: Record<string, { email: string; name: string }> = {};

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, email, display_name, first_name, last_name')
          .in('user_id', userIds);

        profiles?.forEach(p => {
          userMap[p.user_id] = {
            email: p.email || '',
            name: p.display_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown'
          };
        });
      }

      const enrichedRedemptions = data?.map(r => ({
        ...r,
        invite_code: r.invite_codes,
        user_email: userMap[r.user_id]?.email,
        user_name: userMap[r.user_id]?.name,
      })) || [];

      setRedemptions(enrichedRedemptions);
    } catch (err: any) {
      console.error('Error fetching redemptions:', err);
    }
  }, []);

  const generateCode = useCallback(async (prefix: string = 'BETA'): Promise<string | null> => {
    try {
      const { data, error } = await supabase.rpc('generate_invite_code', { prefix });
      if (error) throw error;
      return data;
    } catch (err: any) {
      console.error('Error generating code:', err);
      return null;
    }
  }, []);

  const createCode = useCallback(async (input: CreateInviteCodeInput): Promise<InviteCode | null> => {
    if (!user) return null;

    try {
      // Generate code if not provided
      let code = input.code;
      if (!code) {
        code = await generateCode(input.campaign?.toUpperCase().substring(0, 6) || 'BETA');
        if (!code) throw new Error('Failed to generate code');
      }

      const { data, error } = await supabase
        .from('invite_codes')
        .insert({
          code: code.toUpperCase(),
          tier: input.tier || 'beta_pro',
          duration_days: input.duration_days || 180,
          max_uses: input.max_uses ?? 1,
          campaign: input.campaign || null,
          source: input.source || null,
          notes: input.notes || null,
          expires_at: input.expires_at || null,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Track analytics event
      trackInviteCodeGenerated({
        code: data.code,
        campaign: data.campaign,
        source: data.source,
        duration_days: data.duration_days || 180,
        max_uses: data.max_uses,
        admin_id: user.id,
      });

      await fetchCodes();
      return data;
    } catch (err: any) {
      console.error('Error creating invite code:', err);
      throw err;
    }
  }, [user, generateCode, fetchCodes]);

  const updateCode = useCallback(async (
    id: string,
    updates: Partial<CreateInviteCodeInput & { is_active: boolean }>
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('invite_codes')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      await fetchCodes();
      return true;
    } catch (err: any) {
      console.error('Error updating invite code:', err);
      return false;
    }
  }, [fetchCodes]);

  const deactivateCode = useCallback(async (id: string): Promise<boolean> => {
    // Find the code to get details for analytics
    const codeToDeactivate = codes.find(c => c.id === id);
    const result = await updateCode(id, { is_active: false });
    
    if (result && codeToDeactivate && user) {
      trackInviteCodeDeactivated({
        code: codeToDeactivate.code,
        admin_id: user.id,
        total_redemptions: codeToDeactivate.current_uses,
      });
    }
    
    return result;
  }, [updateCode, codes, user]);

  const activateCode = useCallback(async (id: string): Promise<boolean> => {
    return updateCode(id, { is_active: true });
  }, [updateCode]);

  const deleteCode = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('invite_codes')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await fetchCodes();
      return true;
    } catch (err: any) {
      console.error('Error deleting invite code:', err);
      return false;
    }
  }, [fetchCodes]);

  const redeemCode = useCallback(async (code: string): Promise<{
    success: boolean;
    message: string;
    tier?: string;
    expires_at?: string;
  }> => {
    if (!user) {
      return { success: false, message: 'You must be logged in to redeem a code.' };
    }

    const normalizedCode = code.toUpperCase();

    try {
      const { data, error } = await supabase.rpc('redeem_invite_code', {
        p_code: normalizedCode,
        p_user_id: user.id
      });

      if (error) throw error;

      // Parse the JSON result from the RPC function
      const result = typeof data === 'string' ? JSON.parse(data) : data;
      
      if (result.success) {
        // Track successful redemption
        // Note: Campaign/source data is not exposed via RPC for security
        // We use the duration from the result instead
        trackInviteCodeRedeemed({
          code: normalizedCode,
          campaign: undefined, // Not exposed for security
          source: undefined,   // Not exposed for security  
          user_id: user.id,
          duration_days: result.duration_days || 180,
        });

        // If successful, trigger the beta welcome email
        if (result.expires_at) {
          // Fire and forget - don't wait for email to send
          supabase.functions.invoke('send-beta-welcome', {
            body: {
              userId: user.id,
              expirationDate: result.expires_at,
            },
          }).then(({ error: emailError }) => {
            if (emailError) {
              console.error('Failed to send beta welcome email:', emailError);
            } else {
              console.log('Beta welcome email triggered successfully');
            }
          });
        }
      } else {
        // Track failed redemption
        const errorType = result.error === 'invalid_code' ? 'invalid' :
                         result.error === 'code_expired' ? 'expired' :
                         result.error === 'code_exhausted' ? 'exhausted' :
                         result.error === 'already_redeemed' ? 'already_redeemed' : 'unknown';
        
        trackInviteCodeFailed({
          code_attempted: normalizedCode,
          error_type: errorType,
          user_id: user.id,
        });
      }
      
      return result as {
        success: boolean;
        message: string;
        tier?: string;
        expires_at?: string;
      };
    } catch (err: any) {
      console.error('Error redeeming code:', err);
      
      // Check for duplicate redemption constraint violation
      const errorMessage = err.message || '';
      if (errorMessage.includes('invite_code_redemptions_code_id_user_id_key') || 
          errorMessage.includes('duplicate key')) {
        trackInviteCodeFailed({
          code_attempted: normalizedCode,
          error_type: 'already_redeemed',
          user_id: user.id,
        });
        return { 
          success: false, 
          message: 'You have already redeemed this code.' 
        };
      }
      
      // Track unknown failure
      trackInviteCodeFailed({
        code_attempted: normalizedCode,
        error_type: 'unknown',
        user_id: user.id,
      });
      
      return { success: false, message: err.message || 'Failed to redeem code.' };
    }
  }, [user]);

  // Get statistics
  const getStats = useCallback(() => {
    const totalCodes = codes.length;
    const activeCodes = codes.filter(c => c.is_active).length;
    const totalRedemptions = codes.reduce((sum, c) => sum + c.current_uses, 0);
    const expiredCodes = codes.filter(c => 
      c.expires_at && new Date(c.expires_at) < new Date()
    ).length;

    // Group by campaign
    const byCampaign = codes.reduce((acc, c) => {
      const campaign = c.campaign || 'Uncategorized';
      if (!acc[campaign]) {
        acc[campaign] = { codes: 0, redemptions: 0 };
      }
      acc[campaign].codes++;
      acc[campaign].redemptions += c.current_uses;
      return acc;
    }, {} as Record<string, { codes: number; redemptions: number }>);

    return { totalCodes, activeCodes, totalRedemptions, expiredCodes, byCampaign };
  }, [codes]);

  useEffect(() => {
    fetchCodes();
    fetchRedemptions();
  }, [fetchCodes, fetchRedemptions]);

  return {
    codes,
    redemptions,
    loading,
    error,
    fetchCodes,
    fetchRedemptions,
    generateCode,
    createCode,
    updateCode,
    deactivateCode,
    activateCode,
    deleteCode,
    redeemCode,
    getStats,
  };
}
